"use client";

import { getUserApps } from "@pex/database/queries/app-access";
import type { AppContextType, User as PexUser } from "@pex/types";
import type { Session } from "@supabase/supabase-js";
import { useRouter } from "next/navigation";
import {
  createContext,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { AuthState } from "../types";
import { buildOtpOptions, type OtpMetadata } from "./otp";
import { getSupabaseBrowserClient } from "./supabase";

export const AuthContext = createContext<
  AuthState & {
    // App access is resolved once per authenticated user here (not per
    // consumer mount) so the nav controls' access check doesn't re-run the
    // getUserApps queries on every page navigation (#449). useAppAccess reads
    // these off the context.
    userApps: AppContextType[];
    appsLoading: boolean;
    supabase: ReturnType<typeof getSupabaseBrowserClient>;
    sendOtp: (
      email: string,
      metadata?: OtpMetadata,
      options?: { shouldCreateUser?: boolean }
    ) => Promise<{ error: Error | null }>;
    verifyOtp: (
      email: string,
      token: string
    ) => Promise<{ error: Error | null }>;
    signInWithGoogle: (returnTo?: string) => Promise<{ error: Error | null }>;
    signOut: () => Promise<void>;
  }
>({
  user: null,
  session: null,
  loading: true,
  userApps: [],
  appsLoading: true,
  supabase: null as unknown as ReturnType<typeof getSupabaseBrowserClient>,
  sendOtp: async () => ({ error: null }),
  verifyOtp: async () => ({ error: null }),
  signInWithGoogle: async () => ({ error: null }),
  signOut: async () => {
    // Placeholder - actual implementation provided by AuthProvider
  },
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PexUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userApps, setUserApps] = useState<AppContextType[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const router = useRouter();

  // Use useMemo to ensure client is only created once, even in Strict Mode
  const supabase = useMemo(() => getSupabaseBrowserClient(), []);

  const userId = user?.id ?? null;

  // Resolve app access once per user, not per consumer mount (#449). Because
  // the provider persists across in-app navigations, this fires on login/user
  // change only — the nav controls no longer re-run getUserApps on every page.
  useEffect(() => {
    if (!userId) {
      setUserApps([]);
      setAppsLoading(false);
      return;
    }

    let cancelled = false;
    // Re-enter loading on each user change so consumers don't read stale apps
    // (mirrors the previous useAppAccess behaviour — see #235).
    setAppsLoading(true);
    getUserApps(supabase, userId)
      .then((apps) => {
        if (!cancelled) {
          setUserApps(apps);
          setAppsLoading(false);
        }
      })
      .catch(() => {
        // Non-fatal: leave userApps empty so conditional apps stay hidden.
        if (!cancelled) {
          setUserApps([]);
          setAppsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userId, supabase]);

  useEffect(() => {
    // Helper to convert Supabase auth user to PexUser
    function mapAuthUserToPexUser(
      authUser: NonNullable<Session["user"]>
    ): PexUser {
      const firstName: string | null =
        authUser.user_metadata?.first_name ||
        (
          authUser.user_metadata?.full_name ?? authUser.user_metadata?.name
        )?.split(" ")[0] ||
        null;
      const lastName: string | null =
        authUser.user_metadata?.last_name ||
        (authUser.user_metadata?.full_name ?? authUser.user_metadata?.name)
          ?.split(" ")
          .slice(1)
          .join(" ") ||
        null;
      return {
        id: authUser.id,
        email: authUser.email || "",
        first_name: firstName,
        last_name: lastName,
        avatar_url: authUser.user_metadata?.avatar_url || null,
        created_at: authUser.created_at,
        updated_at: authUser.updated_at || authUser.created_at,
      };
    }

    function setStableUser(authUser: NonNullable<Session["user"]> | null) {
      if (!authUser) {
        setUser(null);
        return;
      }
      const next = mapAuthUserToPexUser(authUser);
      setUser((prev) => (prev?.id === next.id ? prev : next));
    }

    // Get initial session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setStableUser(session?.user ?? null);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error getting session:", error);
        setLoading(false);
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setStableUser(session?.user ?? null);
      // Refresh RSCs on explicit identity changes only.
      // SIGNED_IN is intentionally excluded: @supabase/auth-js fires SIGNED_IN
      // during _recoverAndRefresh on every page load when a stored session exists
      // — that is session recovery, not a new sign-in. The middleware proxy
      // already handles token refresh on every request via getClaims(), so no
      // RSC refresh is needed here. Real sign-ins in PEx apps use redirect-based
      // flows (accounts → app), so the first page render already has the session.
      if (event === "SIGNED_OUT" || event === "USER_UPDATED") {
        router.refresh();
      }
    });

    return () => subscription.unsubscribe();
  }, [supabase, router]);

  const signInWithGoogle = async (returnTo?: string) => {
    const callbackUrl = `${window.location.origin}/auth/callback`;
    const redirectTo = returnTo
      ? `${callbackUrl}?next=${encodeURIComponent(returnTo)}`
      : callbackUrl;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    return { error };
  };

  const sendOtp = async (
    email: string,
    metadata?: OtpMetadata,
    options?: { shouldCreateUser?: boolean }
  ) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: buildOtpOptions(metadata, options),
    });
    return { error };
  };

  const verifyOtp = async (email: string, token: string) => {
    // "email" verifies magic-link/OTP sign-ins and confirmed signups. With
    // "Confirm email" on (GH #987), a brand-new signup's confirmation token can be
    // minted as "signup" type in some GoTrue versions; fall back to that so first
    // signups still verify. The first call does not consume the token on a
    // type-mismatch error, so retrying is safe. Both failing returns the primary
    // ("email") error, the expected type for existing users.
    const primary = await supabase.auth.verifyOtp({
      email,
      token,
      type: "email",
    });
    if (!primary.error) {
      return { error: null };
    }

    const fallback = await supabase.auth.verifyOtp({
      email,
      token,
      type: "signup",
    });
    return { error: fallback.error ? primary.error : null };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      setUser(null);
      setSession(null);
    } catch (error) {
      // Still clear local state even if sign out fails
      setUser(null);
      setSession(null);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        userApps,
        appsLoading,
        supabase,
        sendOtp,
        verifyOtp,
        signInWithGoogle,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
